import { ApiResponseError, AxiosRetryClient } from '@reggieofarrell/axios-retry-client';
import { forceRefreshUser, getToken, isTokenExpiringSoon } from 'utils/firebaseAuth';

/** Marks a retried request so 401 handling runs at most once per call. */
const RETRY_REQUEST_FLAG = '__isRetryRequest';

/**
 * Express routes are mounted under `/v1` (see firebase hosting rewrite). Env vars may omit that
 * suffix — e.g. ngrok tunnel root `https://controlroom-api.ngrok.io` — so we normalize here once.
 *
 * @param {string | undefined} rawBaseUrl
 * @returns {string | undefined}
 */
const resolveInternalApiBaseUrl = rawBaseUrl => {
  if (!rawBaseUrl) {
    return rawBaseUrl;
  }

  const trimmed = rawBaseUrl.replace(/\/+$/, '');

  return trimmed.endsWith('/v1') ? trimmed : `${trimmed}/v1`;
};

/**
 * Sets `Authorization: Bearer …` on plain header objects or axios v1 `AxiosHeaders`.
 *
 * @param {object} headers
 * @param {string} token
 * @returns {object}
 */
const setAuthorizationHeader = (headers, token) => {
  const nextHeaders = headers ?? {};

  if (typeof nextHeaders.set === 'function') {
    nextHeaders.set('Authorization', `Bearer ${token}`);
    return nextHeaders;
  }

  nextHeaders.Authorization = `Bearer ${token}`;
  return nextHeaders;
};

/**
 * Sets a plain or AxiosHeaders header map entry without wiping other headers.
 *
 * @param {object} headers
 * @param {string} name
 * @param {string} value
 * @returns {object}
 */
const setHeader = (headers, name, value) => {
  const nextHeaders = headers ?? {};

  if (typeof nextHeaders.set === 'function') {
    nextHeaders.set(name, value);
    return nextHeaders;
  }

  nextHeaders[name] = value;
  return nextHeaders;
};

/**
 * Browser GET responses must not be reused from cache for this private API.
 * Merges Cache-Control / Pragma onto the request config (caller may already set them).
 *
 * @param {object} config
 * @returns {object}
 */
const withGetNoCacheHeaders = config => {
  const nextConfig = config ?? {};
  nextConfig.headers = setHeader(nextConfig.headers, 'Cache-Control', 'no-cache');
  nextConfig.headers = setHeader(nextConfig.headers, 'Pragma', 'no-cache');
  return nextConfig;
};

export class InternalApiClient extends AxiosRetryClient {
  constructor() {
    super({
      baseURL: resolveInternalApiBaseUrl(process.env.REACT_APP_INTERNAL_API_URL),
      axiosConfig: {},
    });

    this._setupUnauthorizedRetryInterceptor();
  }

  /**
   * On 401, force-refresh the Firebase ID token and retry the same request once.
   * Covers revocation, clock skew, and stale Redux snapshots — not only expiry.
   */
  _setupUnauthorizedRetryInterceptor() {
    this.axios.interceptors.response.use(
      response => response,
      async err => {
        const status = err.response?.status;
        const config = err.config;

        if (status !== 401 || !config || config[RETRY_REQUEST_FLAG]) {
          return Promise.reject(err);
        }

        try {
          const tokenResult = await forceRefreshUser();

          config[RETRY_REQUEST_FLAG] = true;
          config.headers = setAuthorizationHeader(config.headers, tokenResult.token);
          this.axios.defaults.headers.common.Authorization = `Bearer ${tokenResult.token}`;

          return this.axios(config);
        } catch {
          return Promise.reject(err);
        }
      },
    );
  }

  /**
   * Attach a fresh Bearer token before each request when a user is signed in.
   * Proactively refreshes when the token is near expiry.
   * For GETs, also send no-cache headers so browsers do not reuse stale API JSON.
   */
  async preRequestFilter(requestType, url, data, config) {
    let nextConfig = config ?? {};

    // AxiosRetryClient passes RequestType enum values as strings ("GET", "POST", …).
    if (requestType === 'GET') {
      nextConfig = withGetNoCacheHeaders(nextConfig);
    }

    const tokenResult = await getToken();

    if (!tokenResult) {
      return { data, config: nextConfig };
    }

    let activeToken = tokenResult;

    if (isTokenExpiringSoon(tokenResult)) {
      activeToken = await forceRefreshUser();
    }

    nextConfig.headers = setAuthorizationHeader(nextConfig.headers, activeToken.token);
    this.axios.defaults.headers.common.Authorization = `Bearer ${activeToken.token}`;

    return { data, config: nextConfig };
  }

  auth = {
    loginWithEmail: async ({ email, origin, query }) => {
      return (await this.post('/auth/login-with-email', { email, origin, query })).data;
    },
  };

  users = {
    updateProfile: async profile => {
      return (await this.patch('/users/me', profile)).data;
    },
    changeEmail: async email => {
      return (await this.post('/users/me/actions/change-email', { email })).data;
    },
    getMuralAuthUrl: async redirectUrl => {
      return (await this.post('/users/me/actions/mural-auth-url', { redirectUrl })).data;
    },
    syncMuralAuth: async () => {
      return (await this.post('/users/me/actions/sync-mural-auth')).data;
    },
    disconnectMural: async () => {
      return (await this.post('/users/me/actions/disconnect-mural')).data;
    },
    recordLogin: async (options = {}) => {
      return (await this.post('/users/me/actions/record-login', options)).data;
    },
  };

  hubspot = {
    attendeeUploadSessions: {
      /**
       * Poll HubSpot attendee upload progress.
       * Adds a cache-busting query param (GET no-cache headers come from preRequestFilter).
       *
       * @param {string} sessionId
       */
      get: async sessionId => {
        return (
          await this.get(`/hubspot/attendee-upload-sessions/${sessionId}`, {
            params: { _: Date.now() },
          })
        ).data;
      },
      abort: async sessionId => {
        return (await this.post(`/hubspot/attendee-upload-sessions/${sessionId}/actions/abort`))
          .data;
      },
    },
  };

  volunteer = {
    getProjectData: async projectId => {
      return (await this.get(`/projects/${projectId}/volunteer-page`)).data;
    },
    submitVolunteerSignup: async (projectId, data) => {
      return (await this.post(`/projects/${projectId}/actions/volunteer-signup`, data)).data;
    },
  };

  deals = {
    list: async ({ limit, cursor } = {}) => {
      const params = new URLSearchParams();
      if (limit != null) params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      const query = params.toString();
      return (await this.get(`/deals${query ? `?${query}` : ''}`)).data;
    },
    get: async dealId => {
      return (await this.get(`/deals/${dealId}`)).data;
    },
    /**
     * Resolve a Control Room deal by HubSpot deal id (returns null when none exists).
     * @param {string} hubspotDealId
     */
    getByHubspotDealId: async hubspotDealId => {
      return (await this.get(`/deals/by-hubspot-deal/${hubspotDealId}`)).data;
    },
    /**
     * List delivery projects for a Control Room deal (`{ items }`).
     * @param {string} dealId
     */
    listProjects: async dealId => {
      return (await this.get(`/deals/${dealId}/projects`)).data;
    },
    /**
     * Preflight for signed-contract upload. Returns `{ blockers, url, contractedAt }`
     * (never `cr_payload`). Empty `blockers` means the dropzone may proceed.
     * @param {string} dealId
     */
    getProposalContractGate: async dealId => {
      return (await this.get(`/deals/${dealId}/proposal-contract-gate`)).data;
    },
    create: async dealData => {
      return (await this.post('/deals', dealData)).data;
    },
    update: async (dealId, dealData) => {
      return (await this.patch(`/deals/${dealId}`, dealData)).data;
    },
    delete: async dealId => {
      return (await this.delete(`/deals/${dealId}`)).data;
    },
    uploadSignedContract: async (dealId, { storagePath, action }) => {
      return (
        await this.post(`/deals/${dealId}/actions/upload-signed-contract`, {
          storagePath,
          action,
        })
      ).data;
    },
  };

  companies = {
    list: async ({ limit, cursor } = {}) => {
      const params = new URLSearchParams();
      if (limit != null) params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      const query = params.toString();
      return (await this.get(`/companies${query ? `?${query}` : ''}`)).data;
    },
    get: async companyId => {
      return (await this.get(`/companies/${companyId}`)).data;
    },
    /**
     * Creates a company. Accepts the historical string form used by AddDealForm
     * (`create('Acme')`) or a body object for the dedicated Companies form.
     *
     * @param {string|{ companyName: string, domain?: string, hubspotCompanyId?: string }} companyNameOrData
     */
    create: async companyNameOrData => {
      const body =
        typeof companyNameOrData === 'string'
          ? { companyName: companyNameOrData }
          : companyNameOrData;
      return (await this.post('/companies', body)).data;
    },
    update: async (companyId, companyData) => {
      return (await this.patch(`/companies/${companyId}`, companyData)).data;
    },
    delete: async companyId => {
      return (await this.delete(`/companies/${companyId}`)).data;
    },
  };

  projectMethodologies = {
    create: async data => {
      return (await this.post('/project-methodologies', data)).data;
    },
    update: async (methodologyId, methodologyData) => {
      return (await this.patch(`/project-methodologies/${methodologyId}`, methodologyData)).data;
    },
    delete: async methodologyId => {
      return (await this.delete(`/project-methodologies/${methodologyId}`)).data;
    },
  };

  staffBlockedTimes = {
    create: async blockedTimeData => {
      return (await this.post('/staff-blocked-times', blockedTimeData)).data;
    },
    update: async (blockedTimeId, blockedTimeData) => {
      return (await this.patch(`/staff-blocked-times/${blockedTimeId}`, blockedTimeData)).data;
    },
    delete: async blockedTimeId => {
      return (await this.delete(`/staff-blocked-times/${blockedTimeId}`)).data;
    },
  };

  contractors = {
    /**
     * @param {{ limit?: number, cursor?: string, filter?: { isActive?: boolean } }} [options]
     */
    list: async ({ limit, cursor, filter } = {}) => {
      const params = new URLSearchParams();
      if (limit != null) params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      // Bracket notation matches Express/qs parsing of `filter[isActive]=…`.
      if (filter?.isActive != null) {
        params.set('filter[isActive]', String(filter.isActive));
      }
      const query = params.toString();
      return (await this.get(`/contractors${query ? `?${query}` : ''}`)).data;
    },
    get: async contractorId => {
      return (await this.get(`/contractors/${contractorId}`)).data;
    },
    create: async contractorData => {
      return (await this.post('/contractors', contractorData)).data;
    },
    update: async (contractorId, contractorData) => {
      return (await this.patch(`/contractors/${contractorId}`, contractorData)).data;
    },
    delete: async contractorId => {
      return (await this.delete(`/contractors/${contractorId}`)).data;
    },
    syncCircleUser: async contractorId => {
      return (await this.post(`/contractors/${contractorId}/actions/sync-circle-user`)).data;
    },
  };

  projects = {
    list: async ({ limit, cursor } = {}) => {
      const params = new URLSearchParams();
      if (limit != null) params.set('limit', String(limit));
      if (cursor) params.set('cursor', cursor);
      const query = params.toString();
      return (await this.get(`/projects${query ? `?${query}` : ''}`)).data;
    },
    create: async projectData => {
      return (await this.post('/projects', projectData)).data;
    },
    update: async (projectId, projectData, previousDealId) => {
      return (
        await this.patch(`/projects/${projectId}`, {
          ...projectData,
          ...(previousDealId ? { previousDealId } : {}),
        })
      ).data;
    },
    getAttendees: async projectId => {
      return (await this.get(`/projects/${projectId}/attendees`)).data;
    },
    syncAttendeesFromWp: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/sync-vc-event-attendees`)).data;
    },
    delete: async (projectId, dealId) => {
      const config = dealId ? { data: { dealId } } : undefined;
      return (await this.delete(`/projects/${projectId}`, config)).data;
    },
    reSyncCalendar: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/re-sync-calendar`)).data;
    },
    regenerateRunOfShow: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/regenerate-run-of-show`)).data;
    },
    syncAttendeeList: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/sync-attendee-list`)).data;
    },
    uploadAttendeesToHs: async (projectId, { dryRun, skip, max } = {}) => {
      return (
        await this.post(`/projects/${projectId}/actions/upload-attendees-to-hs`, {
          dryRun,
          skip,
          max,
        })
      ).data;
    },
    refreshMessagingConfig: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/refresh-messaging-config`)).data;
    },
    overrideMessagingSchedule: async projectId => {
      return (await this.post(`/projects/${projectId}/actions/override-messaging-schedule`)).data;
    },
    sendAttendeeMessage: async (projectId, { messageId, attendeeId }) => {
      return (
        await this.post(`/projects/${projectId}/actions/send-attendee-message`, {
          messageId,
          attendeeId,
        })
      ).data;
    },
    sendCohortMessage: async (projectId, { messageId }) => {
      return (
        await this.post(`/projects/${projectId}/actions/send-cohort-message`, {
          messageId,
        })
      ).data;
    },
    jobs: {
      list: async projectId => {
        return (await this.get(`/projects/${projectId}/jobs`)).data;
      },
      create: async (projectId, jobId, job) => {
        return (await this.post(`/projects/${projectId}/jobs`, { jobId, job })).data;
      },
      update: async (projectId, jobId, job) => {
        return (await this.patch(`/projects/${projectId}/jobs/${jobId}`, job)).data;
      },
      delete: async (projectId, jobId) => {
        return (await this.delete(`/projects/${projectId}/jobs/${jobId}`)).data;
      },
      updateAssignee: async (projectId, jobId, contractorId, values) => {
        return (
          await this.patch(`/projects/${projectId}/jobs/${jobId}/assignees/${contractorId}`, values)
        ).data;
      },
      removeAssignee: async (projectId, jobId, contractorId) => {
        return (await this.delete(`/projects/${projectId}/jobs/${jobId}/assignees/${contractorId}`))
          .data;
      },
      createInvite: async (projectId, jobId, contractorId, assignedByEmail) => {
        return (
          await this.post(`/projects/${projectId}/jobs/${jobId}/actions/create-invite`, {
            contractorId,
            assignedByEmail,
          })
        ).data;
      },
      resendInvite: async (projectId, jobId, contractorId) => {
        return (
          await this.post(`/projects/${projectId}/jobs/${jobId}/actions/resend-invite`, {
            contractorId,
          })
        ).data;
      },
      createAndSendStaffingContract: async (projectId, jobId, contractorId) => {
        return (
          await this.post(
            `/projects/${projectId}/jobs/${jobId}/actions/create-and-send-staffing-contract`,
            { contractorId },
          )
        ).data;
      },
      resendStaffingContract: async (projectId, jobId, { signatureRequestId, email }) => {
        return (
          await this.post(`/projects/${projectId}/jobs/${jobId}/actions/resend-staffing-contract`, {
            signatureRequestId,
            email,
          })
        ).data;
      },
      sendJobAcceptedEmail: async (projectId, jobId, contractorId) => {
        return (
          await this.post(`/projects/${projectId}/jobs/${jobId}/actions/send-job-accepted-email`, {
            contractorId,
          })
        ).data;
      },
    },
    volunteers: {
      upsert: async (projectId, volunteerId, volunteer) => {
        return (await this.post(`/projects/${projectId}/volunteers`, { volunteerId, volunteer }))
          .data;
      },
      delete: async (projectId, volunteerId) => {
        return (await this.delete(`/projects/${projectId}/volunteers/${volunteerId}`)).data;
      },
    },
    volunteerRoles: {
      upsert: async (projectId, roleId, role) => {
        return (await this.patch(`/projects/${projectId}/volunteer-roles/${roleId}`, role)).data;
      },
      delete: async (projectId, roleId) => {
        return (await this.delete(`/projects/${projectId}/volunteer-roles/${roleId}`)).data;
      },
    },
    checklists: {
      add: async (projectId, checklistId, checklist) => {
        return (await this.post(`/projects/${projectId}/checklists`, { checklistId, checklist }))
          .data;
      },
      patch: async (projectId, checklistId, checklist) => {
        return (await this.patch(`/projects/${projectId}/checklists/${checklistId}`, checklist))
          .data;
      },
    },
  };

  errorHandler(error, reqType, url) {
    if (error.response?.data) {
      // Keep the JSON body on both `response` (ApiResponseError constructor) and `responseData`
      // so helpers like getApiFieldErrors / getApiErrorMessage have a stable, intentional contract
      // without guessing between axios-shaped and library-shaped errors.
      const responseData = error.response.data;
      const apiError = new ApiResponseError(
        `[${this.name}] ${reqType} ${url} : [${error.response?.status}] ${responseData.message}`,
        error.response.status,
        error.response.data,
      );
      apiError.responseData = responseData;
      throw apiError;
    } else {
      // Call the parent class's handleResponseNotReceivedOrOtherError for other types of errors
      this.handleResponseNotReceivedOrOtherError(error, reqType, url);
    }
  }
}
